import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Manages the mount/animate/unmount lifecycle for overlay components
 * (Sheet, Dialog, Modal, Drawer) that need CSS transitions to work
 * inside shadow DOM (TailwindWrapper).
 *
 * Flow:
 *   isOpen=true  → shouldRender=true → reflow → animateIn=true  (enter animation)
 *   isOpen=false → animateIn=false (exit animation) → onTransitionEnd → shouldRender=false
 *
 * @param isOpen - Whether the component should be visible
 * @param fallbackDuration - Timeout (ms) to unmount if onTransitionEnd never fires (default: 350)
 * @param surfaceKey - When the open surface mounts after `isOpen` (e.g. async data), pass a key that
 *   changes when the DOM with `ref` is ready so the enter animation can run. Optional.
 */
export const useAnimatedMount = (isOpen: boolean, fallbackDuration = 350, surfaceKey?: unknown) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* eslint-disable react-hooks/set-state-in-effect -- Intentional: animation lifecycle requires synchronous state updates */
  // Open: mount DOM first, then trigger animation after reflow
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    } else {
      setAnimateIn(false);
      // Fallback unmount in case transitionend doesn't fire
      fallbackTimer.current = setTimeout(() => setShouldRender(false), fallbackDuration);
      return () => clearTimeout(fallbackTimer.current);
    }
  }, [isOpen, fallbackDuration]);

  // After mount, force reflow then start enter animation. The ref node may appear a render later
  // (e.g. parent returns null until data resolves); use rAF retries and surfaceKey to re-run.
  // setAnimateIn(true) must run after a paint where width is still collapsed, or CSS transition-width
  // never runs (layout effect + sync setState skips the w-0 frame).
  useLayoutEffect(() => {
    if (!shouldRender || !isOpen) {
      return;
    }

    let cancelled = false;
    let frames = 0;
    const maxFrames = 240;

    const commitEnter = () => {
      if (cancelled) {
        return;
      }
      const el = ref.current;
      if (!el) {
        return;
      }
      void el.getBoundingClientRect();
      requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        requestAnimationFrame(() => {
          if (cancelled) {
            return;
          }
          setAnimateIn(true);
        });
      });
    };

    const run = () => {
      if (cancelled) {
        return;
      }
      if (ref.current) {
        commitEnter();
        return;
      }
      frames += 1;
      if (frames < maxFrames) {
        requestAnimationFrame(run);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [shouldRender, isOpen, surfaceKey]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Called from onTransitionEnd — only unmounts after exit animation completes
  const onTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== ref.current) {
        return;
      }
      if (!isOpen) {
        clearTimeout(fallbackTimer.current);
        setShouldRender(false);
      }
    },
    [isOpen]
  );

  return { shouldRender, animateIn, ref, onTransitionEnd };
};
